import React from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { OverlayTrigger, Popover, Tooltip } from "react-bootstrap";
import { useToasts } from "react-toast-notifications";
import { Collection } from "intake_shared/src/enum";
import {
    activeClientTagKeys,
    canManageClientTags,
    CLIENT_TAGS,
    ClientTagHistoryPage,
    ClientTagKey,
    ClientTags as ClientTagsState,
    ClientTagsRequest,
    mergeClientTags,
} from "intake_shared/src/client_tags";
import { AclContext } from "../../../../contexts/AclContext";
import { dbv9, fxs } from "../../../../utils/firestore";
import moment from "../../../../utils/app_moment";
import { Badge } from "../../bits/Badge/Badge";
import { Button } from "../../bits/Button/Button";
import { cn } from "../../../helpers";
import { Dropdown } from "../../bits/Dropdown/Dropdown";
import FilterButton from "../../bits/FilterButton/FilterButton";
import styles from "./ClientTags.module.scss";

const requestTags = httpsCallable<
    ClientTagsRequest,
    ClientTagsState | ClientTagHistoryPage
>(fxs, "httpFunctionClientTags");

export function ClientTagBadges({
    tags = {},
    compact = false,
}: {
    tags?: ClientTagsState;
    compact?: boolean;
}) {
    const activeTags = CLIENT_TAGS.filter(({ key }) => tags[key]?.active);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const tagsRef = React.useRef<HTMLDivElement>(null);
    const [overflowing, setOverflowing] = React.useState(false);
    const collapsed = compact && overflowing;

    React.useLayoutEffect(() => {
        if (!compact) return;
        const container = containerRef.current;
        const tagList = tagsRef.current;
        if (!container || !tagList) return;
        const measure = () => {
            const maxWidth = parseFloat(getComputedStyle(container).maxWidth);
            setOverflowing(tagList.scrollWidth > maxWidth);
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(tagList);
        return () => observer.disconnect();
    }, [compact]);

    return (
        <div
            ref={containerRef}
            className={cn(styles.badges, compact && styles.compact)}
        >
            <div
                className={collapsed ? styles.hiddenTags : undefined}
                aria-hidden={collapsed || undefined}
            >
                <div ref={tagsRef} className={styles.badges}>
                    {activeTags.map((tag) => {
                        const state = tags[tag.key]!;
                        const attribution =
                            state.added_by && state.added_at
                                ? ` Added by ${state.added_by.name} in ${
                                      state.origin_system === "mantle"
                                          ? "Mantle"
                                          : "Intake"
                                  }, ${moment(state.added_at).format(
                                      "MMM D, YYYY, h:mm A"
                                  )}`
                                : "";
                        return (
                            <OverlayTrigger
                                key={tag.key}
                                placement="bottom"
                                trigger={["hover", "focus"]}
                                overlay={
                                    <Tooltip id={`tag-${tag.key}`}>
                                        {tag.description}
                                        {attribution}
                                    </Tooltip>
                                }
                            >
                                <span tabIndex={0}>
                                    <Badge
                                        content={tag.label}
                                        bgColor="#EAEBED"
                                        textColor="#33363B"
                                    />
                                </span>
                            </OverlayTrigger>
                        );
                    })}
                </div>
            </div>
            {collapsed && activeTags.length > 0 && (
                <OverlayTrigger
                    trigger="click"
                    placement="auto"
                    rootClose
                    overlay={
                        <Popover
                            id="client-tags-overflow"
                            className={styles.overflowPopover}
                        >
                            <Popover.Content>
                                <ClientTagBadges tags={tags} />
                            </Popover.Content>
                        </Popover>
                    }
                >
                    <Button
                        variant="normal"
                        size="auto"
                        className={styles.moreTags}
                        aria-label={
                            activeTags.length === 1
                                ? "Show tag"
                                : `Show all ${activeTags.length} tags`
                        }
                    >
                        <Badge
                            content={`${activeTags.length} ${
                                activeTags.length === 1 ? "tag" : "tags"
                            }`}
                        />
                    </Button>
                </OverlayTrigger>
            )}
        </div>
    );
}

export function ClientTagFilter({
    value,
    onChange,
}: {
    value: ClientTagKey[];
    onChange: (keys: ClientTagKey[]) => void;
}) {
    return (
        <Dropdown>
            <Dropdown.Trigger>
                <FilterButton
                    aria-label="Filter by tags"
                    text={value.length ? `Tags (${value.length})` : "Filter by tags"}
                />
            </Dropdown.Trigger>
            <Dropdown.Content width={280}>
                <Dropdown.Item
                    isSelected={value.length === 0}
                    closeMenuOnSelect={false}
                    onClick={() => onChange([])}
                >
                    Clear tag filter
                </Dropdown.Item>
                {CLIENT_TAGS.map((tag) => (
                    <Dropdown.Item
                        key={tag.key}
                        isMulti
                        itemValue={tag.key}
                        values={value}
                        closeMenuOnSelect={false}
                        onClick={() =>
                            onChange(
                                value.includes(tag.key)
                                    ? value.filter((key) => key !== tag.key)
                                    : [...value, tag.key]
                            )
                        }
                    >
                        {tag.label}
                    </Dropdown.Item>
                ))}
            </Dropdown.Content>
        </Dropdown>
    );
}

interface ClientTagsProps {
    clientId: string;
    isInactive?: boolean;
    locked?: boolean;
}

export default function ClientTags({
    clientId,
    isInactive = false,
    locked = false,
}: ClientTagsProps) {
    const acl = React.useContext(AclContext)?.authUser?.acl;
    const allowed = canManageClientTags(acl);
    const { addToast } = useToasts();
    const [tags, setTags] = React.useState<ClientTagsState>({});
    const [ready, setReady] = React.useState(false);
    const [savingTag, setSavingTag] = React.useState<ClientTagKey | null>(null);
    const saving = savingTag !== null;
    const savingRef = React.useRef(false);
    const currentClient = React.useRef(clientId);
    currentClient.current = clientId;

    React.useEffect(() => {
        if (!allowed) return;
        setReady(false);
        return onSnapshot(
            doc(
                dbv9,
                isInactive ? Collection.HISTORY : Collection.CLIENTS,
                clientId
            ),
            (snapshot) => {
                setTags(snapshot.data()?.tags ?? {});
                setReady(true);
            },
            () => {
                setReady(false);
                addToast("Unable to load client tags.", { appearance: "error" });
            }
        );
    }, [clientId, isInactive, allowed, addToast]);

    if (!allowed) return null;

    const toggleTag = async (key: ClientTagKey) => {
        if (savingRef.current || locked || !ready) return;
        savingRef.current = true;
        setSavingTag(key);
        try {
            const result = await requestTags({
                clientId,
                tagKey: key,
                action: tags[key]?.active ? "remove" : "add",
            });
            if (currentClient.current === clientId)
                setTags((previous) =>
                    mergeClientTags(previous, result.data as ClientTagsState)
                );
        } catch {
            addToast("Unable to update the tag. Please try again.", {
                appearance: "error",
            });
        } finally {
            savingRef.current = false;
            setSavingTag(null);
        }
    };

    return (
        <div className={styles.row} aria-busy={saving}>
            <ClientTagBadges tags={tags} />
            <Dropdown>
                <Dropdown.Trigger>
                    <span className={styles.edit}>Edit tags ▾</span>
                </Dropdown.Trigger>
                <Dropdown.Content
                    width="min(360px, calc(100vw - 32px))"
                    maxHeight="min(360px, 60vh)"
                >
                    {CLIENT_TAGS.map((tag) => (
                        <Dropdown.Item
                            key={tag.key}
                            isMulti
                            itemValue={tag.key}
                            values={activeClientTagKeys(tags)}
                            closeMenuOnSelect={false}
                            aria-label={tag.label}
                            aria-pressed={Boolean(tags[tag.key]?.active)}
                            aria-disabled={saving || locked || !ready}
                            disabled={saving || locked || !ready}
                            onClick={() => toggleTag(tag.key)}
                        >
                            <span className={styles.option}>
                                <strong>
                                    {tag.label}
                                    {savingTag === tag.key && " — Saving…"}
                                </strong>
                                <span>{tag.description}</span>
                            </span>
                        </Dropdown.Item>
                    ))}
                </Dropdown.Content>
            </Dropdown>
            {saving && <span role="status">Saving tag…</span>}
        </div>
    );
}
